import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { CircleNotch, CheckCircle, WarningCircle } from "@phosphor-icons/react";

/** Payment state is always checked with Stripe by our backend; URL params alone prove nothing. */
export default function ConsultationComplete() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [state, setState] = useState({ loading: true, paid: false, message: "", bookingUrl: null });

  useEffect(() => {
    let active = true;
    if (!/^cs_[A-Za-z0-9_]{8,240}$/.test(sessionId)) {
      setState({ loading: false, paid: false, message: "Missing or invalid checkout session.", bookingUrl: null });
      return () => { active = false; };
    }
    axios.get(`${API}/public/consultation/payment-status/${encodeURIComponent(sessionId)}`)
      .then(({ data }) => {
        if (active) setState({
          loading: false, paid: data.payment_status === "paid",
          message: data.message || "Payment is still processing. Please check back shortly.",
          bookingUrl: data.payment_status === "paid" && data.booking_url?.startsWith("https://")
            ? data.booking_url : null,
        });
      })
      .catch(() => {
        if (active) setState({ loading: false, paid: false,
          message: "We couldn't verify payment yet. If you were charged, please do not pay again. Refresh this page or contact support@ranktrust.io.",
          bookingUrl: null });
      });
    return () => { active = false; };
  }, [sessionId]);

  return <main className="min-h-screen bg-zinc-50 px-6 py-16">
    <div className="mx-auto max-w-xl rounded-2xl border border-zinc-200 bg-white p-8 shadow-sm">
      <Link to="/" className="text-sm font-bold text-[#2D5BFF]">RankTrust</Link>
      {state.loading ? <>
        <CircleNotch size={36} className="mt-8 animate-spin text-[#2D5BFF]" />
        <h1 className="mt-5 font-display text-3xl font-black">Verifying your payment…</h1>
        <p className="mt-2 text-zinc-600">Please wait while we confirm your consultation purchase.</p>
      </> : state.paid ? <>
        <CheckCircle size={38} weight="fill" className="mt-8 text-emerald-600" />
        <h1 className="mt-5 font-display text-3xl font-black">Your $80 consultation is paid.</h1>
        <p className="mt-3 text-zinc-600">{state.message}</p>
        {state.bookingUrl && <a className="btn-primary mt-7 inline-flex" href={state.bookingUrl}
          target="_blank" rel="noreferrer">Schedule My 30-Minute Consultation</a>}
        <p className="mt-4 text-xs text-zinc-500">Your assessment details will be available for your consultation.</p>
      </> : <>
        <WarningCircle size={38} className="mt-8 text-amber-600" />
        <h1 className="mt-5 font-display text-3xl font-black">Payment not yet confirmed</h1>
        <p className="mt-3 text-zinc-600">{state.message}</p>
        <button type="button" className="btn-primary mt-6" onClick={() => window.location.reload()}>
          Check Again
        </button>
      </>}
    </div>
  </main>;
}
